import type { FlightSearchParams } from 'hooks/useSearchFlights'
import type { LeFlightSavedItemData, TransportOption } from 'tripPlanner/api/types'
import { isRecord } from 'lib/object/objectUtils'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'

interface FlatSearchParams {
  departureAirport?: string
  arrivalAirport?: string
  departureDate?: string
  returnDate?: string
  numAdults?: number
  numChildren?: number
  numInfants?: number
  fareClass?: string
  fareType?: string
}

function isFlatFormat(parsed: any): parsed is FlatSearchParams {
  return parsed && !parsed.itinerary && typeof parsed.departureAirport === 'string'
}

function normaliseSearchParams(parsed: any): FlightSearchParams | undefined {
  if (parsed.itinerary && parsed.occupancies) return parsed as FlightSearchParams

  if (!isFlatFormat(parsed)) return undefined

  const itinerary = [{
    departureAirport: parsed.departureAirport!,
    arrivalAirport: parsed.arrivalAirport!,
    departureDate: parsed.departureDate!,
  }]

  if (parsed.returnDate) {
    itinerary.push({
      departureAirport: parsed.arrivalAirport!,
      arrivalAirport: parsed.departureAirport!,
      departureDate: parsed.returnDate,
    })
  }

  return {
    itinerary,
    occupancies: [{
      adults: parsed.numAdults ?? 1,
      children: parsed.numChildren ?? 0,
      infants: parsed.numInfants ?? 0,
    }],
    fareClass: parsed.fareClass ?? 'economy',
    fareType: parsed.returnDate ? 'return' : undefined,
  }
}

function parseSearchParams(paramsJson: string | undefined): FlightSearchParams | undefined {
  if (!paramsJson) return undefined
  try {
    const parsed = JSON.parse(paramsJson)
    return normaliseSearchParams(parsed)
  } catch {
    return undefined
  }
}

function isBookedFlightOption(option: TransportOption): boolean {
  const isSavedItemDataBooked = option.savedItemData?.isBooked === true
  return ('isBooked' in option && option.isBooked === true) || isSavedItemDataBooked
}

// Recovers currencyCode from the serialized searchParams blob.
function parseSearchParamsCurrency(searchParams: string | undefined): string | undefined {
  if (!searchParams) return undefined
  try {
    const parsed: unknown = JSON.parse(searchParams)
    if (!isRecord(parsed)) return undefined
    const code = parsed.currencyCode
    return typeof code === 'string' && code.length > 0 ? code : undefined
  } catch {
    return undefined
  }
}

interface PriceAndCurrency {
  displayPrice?: number
  priceCurrency?: string
}

// Pairs price+currency from one source (refresh or saved), never mixed.
function resolveShortlistedFlightPriceAndCurrency(args: {
  isReturn: boolean
  savedItemData: LeFlightSavedItemData | undefined
  refreshedData: RefreshedFlight | undefined
}): PriceAndCurrency {
  const { isReturn, savedItemData, refreshedData } = args
  if (!savedItemData) return {}

  const savedPrice = isReturn ? savedItemData.farePrice : savedItemData.price
  const savedCurrency =
    savedItemData.currencyCode
    ?? savedItemData.fareCurrencyCode
    ?? parseSearchParamsCurrency(savedItemData.searchParams)

  const refreshHasPrice = refreshedData?.newPrice !== undefined
  const displayPrice = refreshHasPrice ? refreshedData?.newPrice : savedPrice
  const priceCurrency = refreshHasPrice
    ? refreshedData?.newCurrencyCode
    : savedCurrency

  return { displayPrice, priceCurrency }
}

interface ResolveDrawerPriceArgs {
  isReturn: boolean
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  refreshedData?: RefreshedFlight
}

interface DrawerPriceAndCurrency {
  displayPrice?: number
  currencyCode?: string
}

// Pairs price+currency for the flight details drawer.
function resolveDrawerFlightPriceAndCurrency(args: ResolveDrawerPriceArgs): DrawerPriceAndCurrency {
  const { isReturn, departingJourney, returningJourney, refreshedData } = args

  if (refreshedData?.newPrice !== undefined) {
    return {
      displayPrice: refreshedData.newPrice,
      currencyCode: refreshedData.newCurrencyCode,
    }
  }

  const departingFare = departingJourney.price.adult.totalFare
  const returningFare = returningJourney?.price.adult.totalFare

  if (typeof departingFare !== 'number' || (isReturn && typeof returningFare !== 'number')) {
    return { displayPrice: undefined, currencyCode: departingJourney.currencyCode }
  }

  return {
    displayPrice: isReturn ? departingFare + (returningFare ?? 0) : departingFare,
    currencyCode: departingJourney.currencyCode,
  }
}

export {
  parseSearchParams,
  isBookedFlightOption,
  parseSearchParamsCurrency,
  resolveShortlistedFlightPriceAndCurrency,
  resolveDrawerFlightPriceAndCurrency,
}
